import React, { useEffect, useState, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { executeLaravelFrontAPI, laravelFrontApiUrl, appSecretKey, executeLaravelAPI, userToken, convertToDays } from '../../Utils';
import { Button, Form } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import Cookies from 'js-cookie';
import './ProjectMediaReport.scss';
import AsyncSelect from 'react-select/async';


const scrollToTop = () => {
    window.scrollTo(0, 0);

    if (document.documentElement) {
        document.documentElement.scrollTop = 0;
    }
    if (document.body) {
        document.body.scrollTop = 0;
    }

    // Scroll admin layout containers
    const containers = [
        '.content-wrapper',
        '.main-panel',
        '.page-body-wrapper',
        '.container-scroller'
    ];

    containers.forEach(selector => {
        const element = document.querySelector(selector);
        if (element) {
            element.scrollTop = 0;
        }
    });
};

const ProjectMediaReport = () => {
    const location = useLocation();
    const [mediaData, setMediaData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLocation, setSelectedLocation] = useState('');
    const [selectedDeveloper, setSelectedDeveloper] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [locations, setLocations] = useState([]);
    const [selectProjectStatus, setSelectProjectStatus] = useState('');
    const [selectedConfiguration, setSelectedConfiguration] = useState('');

    const handleProjectStatusChange = (e) => {
        setSelectProjectStatus(e.target.value);
    }
    const handleConfigurationChange = (e) => {
        setSelectedConfiguration(e.target.value);
    }
    const [totalProjects, setTotalProjects] = useState(0);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0
    });
    const [mediaFilters, setMediaFilters] = useState({
        render: '',
        unitplan: '',
        floorplan: '',
        qr: '',
        brochure: '',
        last_updated: ''
    });
    const [mediaSortConfig, setMediaSortConfig] = useState({
        column: null,      // 'render' | 'unitplan' | 'floorplan' | 'qr' | 'last_updated'
        direction: 'asc'   // 'asc' | 'desc'
    });

    // Server-side pagination mode: fetch 20 projects per page
    const isMediaClientSideMode = false;


    // Scroll to top immediately before paint (synchronous)
    useLayoutEffect(() => {
        scrollToTop();
    }, [location.pathname]);

    // Scroll to top when component mounts and when route changes
    useEffect(() => {
        // Scroll immediately
        scrollToTop();

        // Scroll multiple times to ensure it works
        const scrollAttempts = [
            setTimeout(() => scrollToTop(), 0),
            setTimeout(() => scrollToTop(), 50),
            setTimeout(() => scrollToTop(), 100),
            setTimeout(() => scrollToTop(), 200),
            setTimeout(() => scrollToTop(), 300)
        ];

        requestAnimationFrame(() => {
            scrollToTop();
            setTimeout(scrollToTop, 50);
            setTimeout(scrollToTop, 100);
        });

        return () => {
            scrollAttempts.forEach(timeout => clearTimeout(timeout));
        };
    }, [location.pathname]);

    useEffect(() => {
        document.title = 'Project Media Report - BeyondWalls';
        const city = Cookies.get("selectedCityCookie") || "pune";
        fetchLocations(city);
        fetchMediaReport();

        const handleCityChange = (event) => {
            const newCity = event.detail.city || Cookies.get("selectedCityCookie") || "pune";
            setSelectedLocation('');
            setPagination(prev => ({ ...prev, current_page: 1 }));
            scrollToTop();
            setTimeout(scrollToTop, 50);
            setTimeout(scrollToTop, 100);
            fetchLocations(newCity).then(() => {
                fetchMediaReport(newCity);
            });
        };

        window.addEventListener('cityChanged', handleCityChange);

        return () => {
            window.removeEventListener('cityChanged', handleCityChange);
        };
    }, []);

    // Reset pagination to page 1 when any filter changes
    useEffect(() => {
        setPagination(prev => ({
            ...prev,
            current_page: 1
        }));
    }, [selectedLocation, selectedDeveloper, selectProjectStatus, selectedConfiguration, searchTerm, mediaFilters.render, mediaFilters.unitplan, mediaFilters.floorplan, mediaFilters.qr, mediaFilters.last_updated, mediaFilters?.brochure]);

    // When NOT in client-side mode, use server pagination
    useEffect(() => {
        if (!isMediaClientSideMode) {
            fetchMediaReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocation, pagination.current_page, pagination.per_page, selectedDeveloper, selectProjectStatus, selectedConfiguration, searchTerm, isMediaClientSideMode, mediaFilters.render, mediaFilters.unitplan, mediaFilters.floorplan, mediaFilters.qr, mediaFilters?.brochure, mediaFilters.last_updated]);

    // When client-side mode (sorting or search) is enabled, fetch all data once for client-side sorting & pagination
    useEffect(() => {
        if (isMediaClientSideMode) {
            fetchMediaReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMediaClientSideMode, selectedLocation, selectedDeveloper, selectProjectStatus, selectedConfiguration, searchTerm, mediaFilters.render, mediaFilters.unitplan, mediaFilters.floorplan, mediaFilters.qr, mediaFilters?.brochure, mediaFilters.last_updated]);




    const fetchMediaReport = async (cityOverride = null) => {
        try {
            setLoading(true);
            let paramsData = {};
            paramsData.city = Cookies.get("selectedCityCookie") || "pune";
            // When client-side mode (sorting or search) is active, fetch all data for client-side processing
            // Otherwise, use server-side pagination
            paramsData.per_page = isMediaClientSideMode ? 10000 : pagination.per_page;
            paramsData.page = isMediaClientSideMode ? 1 : pagination.current_page;

            // FILTER TYPE LOGIC
            if (searchTerm && searchTerm.trim() !== '') {
                paramsData.searchtype = 'name';
                paramsData.name = searchTerm.trim();
            } else if (selectedLocation) {
                paramsData.searchtype = 'locality_name';
                paramsData.locality_name = selectedLocation;
            } else if (selectedDeveloper && selectedDeveloper.value) {
                paramsData.searchtype = 'developers';
                paramsData.developer = selectedDeveloper.value;
            }

            // Add other filters if they're set
            if (selectProjectStatus) {
                paramsData.project_status = selectProjectStatus;
            }
            if (selectedConfiguration) {
                paramsData.bhk = selectedConfiguration;
                paramsData.type = 'bhk';
            }

            // Add media filters (render, unitplan, floorplan, qr)
            if (mediaFilters.render) {
                paramsData.is_render = mediaFilters.render;
            }
            if (mediaFilters.unitplan) {
                paramsData.is_unitplan = mediaFilters.unitplan;
            }
            if (mediaFilters.floorplan) {
                paramsData.is_floorplan = mediaFilters.floorplan;
            }
            if (mediaFilters.qr) {
                paramsData.is_qr = mediaFilters.qr;
            }
            if (mediaFilters.brochure) {
                paramsData.is_brochure = mediaFilters.brochure;
            }
            // Add Last Updated sorting
            if (mediaFilters.last_updated) {
                if (mediaFilters.last_updated === 'newest') {
                    paramsData.orderby = 'updatedatdesc';
                } else if (mediaFilters.last_updated === 'oldest') {
                    paramsData.orderby = 'updatedatasc';
                }
            }

            console.log('API Params:', paramsData);

            try {
                const result = await executeLaravelFrontAPI('projects', paramsData, 'GET');
                setTotalProjects(result?.data?.total || 0);
                if (result && result.data && Array.isArray(result.data)) {
                    setMediaData(result.data);
                    if (result.data && !isMediaClientSideMode) {
                        setPagination(prev => ({
                            ...prev,
                            current_page: result.current_page || 1,
                            last_page: result.last_page || 1,
                            total: result.total || 0,
                            per_page: result.per_page || prev.per_page
                        }));
                    }
                } else if (result && result.data && result.data.data && Array.isArray(result.data.data)) {
                    setMediaData(result.data.data);
                    if (result.data && !isMediaClientSideMode) {
                        setPagination(prev => ({
                            ...prev,
                            current_page: result.data.current_page || 1,
                            last_page: result.data.last_page || 1,
                            total: result.data.total || 0,
                            per_page: result.data.per_page || prev.per_page
                        }));
                    }
                } else {
                    setMediaData([]);
                }
            } catch (apiError) {
                let apiUrl = laravelFrontApiUrl.endsWith('/')
                    ? `${laravelFrontApiUrl}api/v1/projects`
                    : `${laravelFrontApiUrl}/api/v1/projects`;
                let urlWithParams = new URL(apiUrl);
                Object.keys(paramsData).forEach((key) => {
                    urlWithParams.searchParams.append(key, paramsData[key]);
                });

                let response = await fetch(urlWithParams.toString(), {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-app-key': appSecretKey || ''
                    }
                });

                // If 404, try without /api/v1/ prefix
                if (response.status === 404) {
                    apiUrl = laravelFrontApiUrl.endsWith('/')
                        ? `${laravelFrontApiUrl}projects`
                        : `${laravelFrontApiUrl}/projects`;
                    urlWithParams = new URL(apiUrl);
                    Object.keys(paramsData).forEach((key) => {
                        urlWithParams.searchParams.append(key, paramsData[key]);
                    });
                    response = await fetch(urlWithParams.toString(), {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'x-app-key': appSecretKey || ''
                        }
                    });
                }

                if (response.status === 200) {
                    const result = await response.json();
                    if (result && result.data && Array.isArray(result.data)) {
                        setMediaData(result.data);
                        if (result.data && !isMediaClientSideMode) {
                            setPagination(prev => ({
                                ...prev,
                                current_page: result.current_page || 1,
                                last_page: result.last_page || 1,
                                total: result.total || 0,
                                per_page: result.per_page || prev.per_page
                            }));
                        }
                    } else {
                        setMediaData([]);
                    }
                } else {
                    console.error('API Error:', response.status, response.statusText);
                    setMediaData([]);
                }
            }
        } catch (error) {
            console.error('Error fetching media report:', error);
            setMediaData([]);
        } finally {
            setLoading(false);
        }
    };

    // Helper to parse nested JSON/string arrays for media columns
    function parseStringNestedArray(value) {
        try {
            const data = JSON.parse(value);
            const flat = data.flat();

            const cleaned = flat.filter(item => {
                if (typeof item === "object" && item !== null) {
                    return Object.keys(item).length > 0;
                }
                return true;
            });

            return cleaned;
        } catch (error) {
            return [];
        }
    }

    const handleLocationChange = (e) => {
        setSelectedLocation(e.target.value);
        setPagination(prev => ({ ...prev, current_page: 1 }));
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, current_page: newPage }));
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setMediaFilters(prev => ({
            ...prev,
            [name]: value
        }));
        setPagination(prev => ({ ...prev, current_page: 1 }));
    };

    const handleMediaSort = (column) => {
        setMediaSortConfig(prev => {
            if (prev.column === column) {
                // Toggle direction
                return {
                    column,
                    direction: prev.direction === 'asc' ? 'desc' : 'asc'
                };
            }
            // Default: for Yes/No columns, start with 'asc' (Yes first), for last_updated start with 'asc' (Newest first)
            const defaultDirection = 'asc';
            return {
                column,
                direction: defaultDirection
            };
        });
        // When changing sort, keep current page (server pagination), but sorting is client-side only
    };

    // Media data - all filtering and sorting is now server-side via API
    const filteredMediaData = React.useMemo(() => {
        let data = Array.isArray(mediaData) ? [...mediaData] : [];
        return data;
    }, [mediaData]);

    // When client-side mode (sorting or search) is active, apply client-side pagination on the filtered/sorted data
    const pagedMediaData = React.useMemo(() => {
        if (!isMediaClientSideMode) {
            // When not in client-side mode, filteredMediaData already reflects server-side pagination
            return filteredMediaData;
        }

        const startIndex = (pagination.current_page - 1) * pagination.per_page;
        const endIndex = startIndex + pagination.per_page;
        return filteredMediaData.slice(startIndex, endIndex);
    }, [filteredMediaData, isMediaClientSideMode, pagination.current_page, pagination.per_page]);

    // When client-side mode is enabled, recalculate pagination total/last_page based on filtered data length
    useEffect(() => {
        if (isMediaClientSideMode) {
            setPagination(prev => {
                const total = filteredMediaData.length;
                const lastPage = Math.max(1, Math.ceil(total / prev.per_page));
                const currentPage = Math.min(prev.current_page, lastPage);
                return {
                    ...prev,
                    total,
                    last_page: lastPage,
                    current_page: currentPage
                };
            });
        }
    }, [filteredMediaData.length, mediaSortConfig.column, pagination.per_page]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return dateString;
        }
    };

    const formatReraNumber = (reraNumber) => {
        if (!reraNumber) return 'N/A';
        try {
            if (typeof reraNumber === 'string') {
                const parsed = JSON.parse(reraNumber.replace(/'/g, '"'));
                if (Array.isArray(parsed) && parsed.length > 0) {
                    return parsed[0];
                }
            }
            return reraNumber;
        } catch (error) {
            return reraNumber;
        }
    };

    const fetchLocations = async () => {
        try {
            const params = {
                page: 1,
                per_page: 1000,
                show_all: 1
            };

            const result = await executeLaravelAPI('locations/city/' + (Cookies.get("selectedCityCookie") || "pune"), params, 'GET', userToken);
            setLocations(result?.data || []);

        } catch (error) {
            console.error("Error fetching locations:", error);
        }
    };

    const fetchDevelopers = async (inputValue) => {
        if (inputValue.length > 2) {
            try {
                const paramsDev = {
                    name: inputValue,
                    searchtype: "developers"
                };

                const response = await executeLaravelAPI(
                    "developersinsearch",
                    paramsDev,
                    "GET",
                    userToken
                );

                return response.data.data.map(dev => ({
                    label: dev.name,
                    value: dev.id
                }));

            } catch (error) {
                console.error("Error fetching developers:", error);
                return [];
            }
        }
        return [];
    };

    useEffect(() => {
        fetchLocations();
    }, [])

    const handleDeveloper = (option) => {
        setSelectedDeveloper(option);
        setPagination(prev => ({ ...prev, current_page: 1 })); // optional reset

    };

    const projectStatus = [
        {
            key: 'Under Construction', value: 'Under Construction'
        },
        {
            key: 'Ready To Move', value: 'Ready To Move'
        },
        {
            key: 'New Launch', value: 'New Launch'
        },
        {
            key: 'Nearing Possession', value: 'Nearing Possession'
        }
    ];

    const exportToCSV = () => {
        if (!filteredMediaData || filteredMediaData.length === 0) {
            return;
        }

        const headers = [
            'Project Name',
            'RERA Number',
            'Render',
            'Unit Plan',
            'Floor Plan',
            'QR',
            'Brochure',
            'Last Updated (days)'
        ];

        const rows = filteredMediaData.map((item) => {
            const hasRender = item?.gallery?.length > 0 ? 'Yes' : 'No';
            const hasUnitPlan = parseStringNestedArray(item.unitplan)?.length > 0 ? 'Yes' : 'No';
            const hasFloorPlan = parseStringNestedArray(item.floorplan)?.length > 0 ? 'Yes' : 'No';
            const hasQr = item?.rera_qr_image?.length > 0 ? 'Yes' : 'No';
            const lastUpdatedDays = `${convertToDays(item?.updated_at)} days`;

            const cells = [
                item.name || 'N/A',
                formatReraNumber(item.rera_registration_no),
                hasRender,
                hasUnitPlan,
                hasFloorPlan,
                hasQr,
                lastUpdatedDays
            ];

            return cells
                .map((cell) => {
                    const value = cell == null ? '' : String(cell);
                    const escaped = value.replace(/"/g, '""');
                    return `"${escaped}"`;
                })
                .join(',');
        });

        const csvContent = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'project-media-report.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const clearFilters = () => {
        setSelectedLocation("");
        setSelectedDeveloper(null);
        setSelectProjectStatus("");
        setSelectedConfiguration("");
        setSearchTerm("");
        setMediaFilters({
            render: '',
            unitplan: '',
            floorplan: '',
            qr: '',
            brochure: '',
            last_updated: ''
        });
        setMediaSortConfig({
            column: null,
            direction: 'asc'
        });

        // Reset pagination to page 1
        setPagination(prev => ({
            ...prev,
            current_page: 1
        }));

        // Fetch fresh data without any filter values
        fetchMediaReport();
    };

    return (
        <div className="project-media-report">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="page-header mb-4">
                            <h2 className="page-title">Project Media Report</h2>
                        </div>

                        {/* Filters */}
                        <div className="filters-section mb-4">
                            <div className="row mb-3 align-items-center">
                                <div className="col-lg-8">
                                    <div className='position-relative'>
                                        <input
                                            type="search"
                                            className='form-control search-input shadow-none border-none'
                                            placeholder="Search by Project Name"
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                            autoComplete='off'
                                            style={{
                                                background: "#ffffff url('https://api.iconify.design/ion/search-outline.svg') no-repeat 10px center / 24px",
                                                height: '48px'
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="col-lg-4 text-end">
                                    <Button
                                        variant="outline-primary"
                                        onClick={exportToCSV}
                                        disabled={loading || !filteredMediaData.length}
                                        style={{ height: '48px', whiteSpace: 'nowrap', fontSize: '16px', padding: '0 24px' }}
                                    >
                                        <Icon icon="mdi:file-export-outline" className="me-1" style={{ fontSize: '20px' }} />
                                        Export CSV
                                    </Button>
                                </div>
                            </div>

                            <div className="row">

                                <div className="col-md-3">
                                    <Form.Group>
                                        <Form.Label>Location</Form.Label>
                                        <Form.Select
                                            value={selectedLocation}
                                            onChange={handleLocationChange}
                                        >
                                            <option value="">All Locations</option>
                                            {locations.map((location) => (
                                                <option key={location.id} value={location.location}>
                                                    {location.location}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                                <div className="col-md-3">
                                    <Form.Group>
                                        <Form.Label>Developers</Form.Label>

                                        <AsyncSelect
                                            cacheOptions
                                            defaultOptions
                                            loadOptions={fetchDevelopers}
                                            value={selectedDeveloper}
                                            onChange={handleDeveloper}
                                            isClearable
                                            placeholder="Select Developer"
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    minHeight: '38px',
                                                    height: '38px',
                                                    borderColor: '#ced4da',
                                                    borderRadius: '0.375rem',
                                                    fontSize: '1rem',
                                                    fontWeight: '400',
                                                    lineHeight: '1.5',
                                                    color: '#212529',
                                                    backgroundColor: '#fff',
                                                    boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
                                                    '&:hover': {
                                                        borderColor: '#ced4da'
                                                    }
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    height: '38px',
                                                    padding: '0.375rem 0.75rem'
                                                }),
                                                input: (base) => ({
                                                    ...base,
                                                    margin: '0',
                                                    padding: '0',
                                                    fontSize: '1rem'
                                                }),
                                                indicatorsContainer: (base) => ({
                                                    ...base,
                                                    height: '38px'
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color: '#6c757d',
                                                    fontSize: '1rem'
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color: '#212529',
                                                    fontSize: '1rem'
                                                })
                                            }}
                                        />
                                    </Form.Group>

                                </div>
                                <div className="col-md-2">
                                    <Form.Group>
                                        <Form.Label>Project Status</Form.Label>
                                        <Form.Select
                                            value={selectProjectStatus}
                                            onChange={handleProjectStatusChange}
                                        >
                                            <option value="">select</option>
                                            {projectStatus.map((item) => (
                                                <option key={item.key} value={item.key}>
                                                    {item.value}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                                <div className="col-md-2">
                                    <Form.Group>
                                        <Form.Label>Configuration</Form.Label>
                                        <Form.Select
                                            value={selectedConfiguration}
                                            onChange={handleConfigurationChange}
                                        >
                                            <option value="">Select</option>
                                            <option value="1">1 BHK</option>
                                            <option value="2">2 BHK</option>
                                            <option value="3">3 BHK</option>
                                            <option value="4">4 BHK</option>
                                            <option value="5">5 BHK</option>
                                            <option value="6">6 BHK</option>
                                            <option value="7">7 BHK</option>
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                                <div className="col-md-2 d-flex align-items-end">
                                    <Button variant='success' onClick={clearFilters} >
                                        Clear Filters
                                    </Button>
                                </div>

                            </div>


                        </div>

                        {/* Table */}
                        <div className="box-body table-container mt-4 hr-scroll-style">
                            <table className="table table-resposive table-hover dashboard-table shadow-sm mb-0">
                                <thead>
                                    <tr>
                                        <th>
                                            <div className="d-flex align-items-center">
                                                Project name

                                            </div>
                                        </th>
                                        <th>
                                            <div className="d-flex align-items-center">
                                                Rera number

                                            </div>
                                        </th>
                                        <th >
                                            <div className="d-flex align-items-center justify-content-between">
                                                <span>Render</span>
                                                <Form.Select
                                                    size="sm"
                                                    name="render"
                                                    value={mediaFilters.render}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleFilterChange(e);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{ width: '70px', fontSize: '0.75rem', padding: '2px 4px' }}
                                                >
                                                    <option value="">All</option>
                                                    <option value="yes">Yes</option>
                                                    <option value="no">No</option>
                                                </Form.Select>
                                            </div>
                                        </th>
                                        <th >
                                            <div className="d-flex align-items-center justify-content-between">
                                                <span>Unit Plan</span>
                                                <Form.Select
                                                    size="sm"
                                                    name="unitplan"
                                                    value={mediaFilters.unitplan}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleFilterChange(e);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{ width: '70px', fontSize: '0.75rem', padding: '2px 4px' }}
                                                >
                                                    <option value="">All</option>
                                                    <option value="yes">Yes</option>
                                                    <option value="no">No</option>
                                                </Form.Select>
                                            </div>
                                        </th>
                                        <th >
                                            <div className="d-flex align-items-center justify-content-between">
                                                <span>Floor Plan</span>
                                                <Form.Select
                                                    size="sm"
                                                    name="floorplan"
                                                    value={mediaFilters.floorplan}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleFilterChange(e);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{ width: '70px', fontSize: '0.75rem', padding: '2px 4px' }}
                                                >
                                                    <option value="">All</option>
                                                    <option value="yes">Yes</option>
                                                    <option value="no">No</option>
                                                </Form.Select>
                                            </div>
                                        </th>
                                        <th >
                                            <div className="d-flex align-items-center justify-content-between">
                                                <span>QR</span>
                                                <Form.Select
                                                    size="sm"
                                                    name="qr"
                                                    value={mediaFilters.qr}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleFilterChange(e);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{ width: '70px', fontSize: '0.75rem', padding: '2px 4px' }}
                                                >
                                                    <option value="">All</option>
                                                    <option value="yes">Yes</option>
                                                    <option value="no">No</option>
                                                </Form.Select>
                                            </div>
                                        </th>
                                        <th >
                                            <div className="d-flex align-items-center justify-content-between">
                                                <span>Brochure</span>
                                                <Form.Select
                                                    size="sm"
                                                    name="brochure"
                                                    value={mediaFilters.brochure}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleFilterChange(e);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{ width: '70px', fontSize: '0.75rem', padding: '2px 4px' }}
                                                >
                                                    <option value="">All</option>
                                                    <option value="yes">Yes</option>
                                                    <option value="no">No</option>
                                                </Form.Select>
                                            </div>
                                        </th>
                                        <th >
                                            <div className="d-flex align-items-center justify-content-between">
                                                <span>Last updated</span>
                                                <Form.Select
                                                    size="sm"
                                                    name="last_updated"
                                                    value={mediaFilters.last_updated}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleFilterChange(e);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{ width: '100px', fontSize: '0.75rem', padding: '2px 4px' }}
                                                >
                                                    <option value="">All</option>
                                                    <option value="newest">Newest</option>
                                                    <option value="oldest">Oldest</option>
                                                </Form.Select>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className={loading ? 'skeleton-loading' : ''}>
                                    {loading ? (
                                        Array.from({ length: 10 }).map((_, index) => (
                                            <tr key={`skeleton-${index}`} className="skeleton-row" style={{ height: '48px' }}>
                                                <td style={{ height: '48px', padding: '12px 15px' }}>
                                                    <div className="skeleton-loader" style={{ width: '60%', height: '16px', marginTop: '8px', minHeight: '16px' }}></div>
                                                </td>
                                                <td style={{ height: '48px', padding: '12px 15px' }}>
                                                    <div className="skeleton-loader" style={{ width: '70%', height: '16px', marginTop: '8px', minHeight: '16px' }}></div>
                                                </td>
                                                <td style={{ height: '48px', padding: '12px 15px' }}>
                                                    <div className="skeleton-loader" style={{ width: '40%', height: '24px', borderRadius: '12px', margin: '0 auto', marginTop: '4px', minHeight: '24px' }}></div>
                                                </td>
                                                <td style={{ height: '48px', padding: '12px 15px' }}>
                                                    <div className="skeleton-loader" style={{ width: '40%', height: '24px', borderRadius: '12px', margin: '0 auto', marginTop: '4px', minHeight: '24px' }}></div>
                                                </td>
                                                <td style={{ height: '48px', padding: '12px 15px' }}>
                                                    <div className="skeleton-loader" style={{ width: '40%', height: '24px', borderRadius: '12px', margin: '0 auto', marginTop: '4px', minHeight: '24px' }}></div>
                                                </td>
                                                <td style={{ height: '48px', padding: '12px 15px' }}>
                                                    <div className="skeleton-loader" style={{ width: '40%', height: '24px', borderRadius: '12px', margin: '0 auto', marginTop: '4px', minHeight: '24px' }}></div>
                                                </td>
                                                <td style={{ height: '48px', padding: '12px 15px' }}>
                                                    <div className="skeleton-loader" style={{ width: '60%', height: '16px', marginTop: '8px', minHeight: '16px' }}></div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : pagedMediaData.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-5">
                                                {searchTerm ? 'No matching project found' : 'No data found'}
                                            </td>
                                        </tr>
                                    ) : (
                                        pagedMediaData.map((item, index) => {
                                            return (
                                                <tr key={item.project_id || index}>
                                                    <td>{item.name || 'N/A'}</td>
                                                    <td>{formatReraNumber(item.rera_registration_no)}</td>
                                                    <td>
                                                        <span
                                                            className={`badge ${Array.isArray(item?.gallery) &&
                                                                item.gallery.length > 0 &&
                                                                item.gallery.every(g => g?.id)
                                                                ? 'bg-success'
                                                                : 'bg-secondary'
                                                                }`}
                                                        >
                                                            {Array.isArray(item?.gallery) &&
                                                                item.gallery.length > 0 &&
                                                                item.gallery.every(g => g?.id)
                                                                ? "Yes"
                                                                : "No"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span
                                                            className={`badge ${parseStringNestedArray(item.unitplan)?.length > 0 ? "bg-success" : "bg-secondary"
                                                                }`}
                                                        >


                                                            {parseStringNestedArray(item.unitplan)?.length > 0 ? "Yes" : "No"}
                                                        </span>
                                                    </td>

                                                    <td>
                                                        <span
                                                            className={`badge ${parseStringNestedArray(item.floorplan)?.length > 0 ? "bg-success" : "bg-secondary"
                                                                }`}
                                                        >


                                                            {parseStringNestedArray(item.floorplan)?.length > 0 ? "Yes" : "No"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${item.rera_qr_image?.length > 0 ? 'bg-success' : 'bg-secondary'}`}>
                                                            {item.rera_qr_image?.length > 0 ? "Yes" : 'No'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span
                                                            className={`badge ${Array.isArray(item?.brochure) &&
                                                                item.brochure.length > 0 &&
                                                                item.brochure.every(g => g?.id)
                                                                ? 'bg-success'
                                                                : 'bg-secondary'
                                                                }`}
                                                        >
                                                            {Array.isArray(item?.brochure) &&
                                                                item.brochure.length > 0 &&
                                                                item.brochure.every(g => g?.id)
                                                                ? "Yes"
                                                                : "No"}
                                                        </span>
                                                    </td>
                                                    <td>{`${convertToDays(item?.updated_at)} days`}</td>
                                                </tr>
                                            )
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {!loading && (filteredMediaData.length > 0 || pagination.last_page > 1) && (
                            <div className="pagination-section mt-4">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="pagination-info">
                                        {(() => {
                                            // All filtering and sorting is now server-side
                                            const start = ((pagination.current_page - 1) * pagination.per_page) + 1;
                                            const end = Math.min(pagination.current_page * pagination.per_page, pagination.total);
                                            return `Showing ${start} to ${end} of ${pagination.total} entries`;
                                        })()}
                                    </div>
                                    {pagination.last_page > 1 && (
                                        <nav>
                                            <ul className="pagination mb-0">
                                                <li className={`page-item ${pagination.current_page === 1 ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(pagination.current_page - 1)}
                                                        disabled={pagination.current_page === 1}
                                                    >
                                                        Previous
                                                    </button>
                                                </li>
                                                {[...Array(pagination.last_page)].map((_, index) => {
                                                    const page = index + 1;
                                                    if (
                                                        page === 1 ||
                                                        page === pagination.last_page ||
                                                        (page >= pagination.current_page - 2 && page <= pagination.current_page + 2)
                                                    ) {
                                                        return (
                                                            <li key={page} className={`page-item ${pagination.current_page === page ? 'active' : ''}`}>
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => handlePageChange(page)}
                                                                >
                                                                    {page}
                                                                </button>
                                                            </li>
                                                        );
                                                    } else if (
                                                        page === pagination.current_page - 3 ||
                                                        page === pagination.current_page + 3
                                                    ) {
                                                        return (
                                                            <li key={page} className="page-item disabled">
                                                                <span className="page-link">...</span>
                                                            </li>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                                <li className={`page-item ${pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(pagination.current_page + 1)}
                                                        disabled={pagination.current_page === pagination.last_page}
                                                    >
                                                        Next
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectMediaReport;

